import PropTypes from "prop-types";
import { useCallback, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";

import PanelCard from "layouts/dutyEstimator/components/PanelCard";
import ageBandTableData from "layouts/dutyEstimator/data/ageBandTableData";
import { INPUT_SX, WHITE_BUTTON_SX, GREEN_BUTTON_SX } from "layouts/dutyEstimator/constants";

const EMPTY_FORM = { label: "", minAgeMonths: "", maxAgeMonths: "", rate: "", isActive: true };

// Drives both the Section 3 depreciation table and the three Section 4 penalty tables. They
// share the same shape — a label, an inclusive month range and one rate — so one panel serves both.
function AgeBandsPanel({ title, caption, bands, rateKey, rateLabel, extraPayload, header, onSave, onDelete, onValidationError }) {
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const handleAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const handleEdit = useCallback(
    (band) => {
      setEditingId(band?.id);
      setForm({
        label: band?.label ?? "",
        minAgeMonths: band?.minAgeMonths ?? "",
        maxAgeMonths: band?.maxAgeMonths ?? "",
        rate: band?.[rateKey] ?? "",
        isActive: band?.isActive !== false,
      });
      setFormOpen(true);
    },
    [rateKey]
  );

  const handleSubmit = async () => {
    if (!form.label.trim() || form.minAgeMonths === "" || form.rate === "") {
      onValidationError("Please fill in the band name, starting age and rate.");
      return;
    }
    if (form.maxAgeMonths !== "" && Number(form.minAgeMonths) > Number(form.maxAgeMonths)) {
      onValidationError("The starting age cannot be later than the ending age.");
      return;
    }

    const saved = await onSave(editingId, {
      ...extraPayload,
      label: form.label.trim(),
      minAgeMonths: Number(form.minAgeMonths),
      // Blank means the band is open-ended, which the API expects as null.
      maxAgeMonths: form.maxAgeMonths === "" ? null : Number(form.maxAgeMonths),
      [rateKey]: Number(form.rate),
      isActive: form.isActive,
    });
    if (saved) setFormOpen(false);
  };

  const { columns, rows } = useMemo(
    () => ageBandTableData({ bandList: bands, rateKey, rateLabel, onEdit: handleEdit, onDelete }),
    [bands, rateKey, rateLabel, handleEdit, onDelete]
  );

  // Months are the storage unit, but the source tables are written in years, so show the
  // conversion live while the admin types.
  const previewYears = (months) => (months === "" || months === null ? "" : `${(Number(months) / 12).toFixed(1)} years`);

  return (
    <>
      <PanelCard
        title={title}
        caption={caption}
        action={
          <Button variant="contained" size="small" onClick={handleAdd} sx={WHITE_BUTTON_SX}>
            Add Band
          </Button>
        }
      >
        {header}
        <MDBox pt={header ? 1 : 3}>
          <DataTable
            table={{ columns, rows }}
            isSorted={false}
            entriesPerPage={{ defaultValue: 10, entries: [10, 15, 25] }}
            showTotalEntries={false}
            noEndBorder
            canSearch={false}
          />
        </MDBox>
      </PanelCard>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>{editingId ? "Edit Band" : "Add Band"}</DialogTitle>
        <DialogContent>
          <MDTypography variant="caption" color="text" display="block" mb={2}>
            Ages are entered in months and include both ends. Leave the ending age blank for the final,
            open-ended band. Bands may not overlap — the server will reject one that does.
          </MDTypography>

          <Grid container spacing={2} pt={1}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                fullWidth
                label="Band Name"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                helperText="Shown to buyers, e.g. Over 12 to 15 years"
                sx={INPUT_SX}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="From Age (months)"
                value={form.minAgeMonths}
                onChange={(e) => setForm({ ...form, minAgeMonths: e.target.value })}
                helperText={previewYears(form.minAgeMonths) || "Inclusive"}
                sx={INPUT_SX}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="To Age (months)"
                value={form.maxAgeMonths}
                onChange={(e) => setForm({ ...form, maxAgeMonths: e.target.value })}
                helperText={previewYears(form.maxAgeMonths) || "Blank = open-ended"}
                sx={INPUT_SX}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label={rateLabel}
                value={form.rate}
                onChange={(e) => setForm({ ...form, rate: e.target.value })}
                sx={INPUT_SX}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <MDBox display="flex" alignItems="center" height="100%">
                <FormControlLabel
                  control={
                    <Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                  }
                  label={
                    <MDTypography variant="button" fontWeight="regular">
                      Active
                    </MDTypography>
                  }
                />
              </MDBox>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} sx={GREEN_BUTTON_SX}>
            {editingId ? "Save Changes" : "Add Band"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

AgeBandsPanel.defaultProps = { caption: "", extraPayload: {}, header: null };

AgeBandsPanel.propTypes = {
  title: PropTypes.string.isRequired,
  caption: PropTypes.string,
  bands: PropTypes.array.isRequired,
  rateKey: PropTypes.string.isRequired,
  rateLabel: PropTypes.string.isRequired,
  extraPayload: PropTypes.object,
  header: PropTypes.node,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onValidationError: PropTypes.func.isRequired,
};

export default AgeBandsPanel;
